import type { ReactNode } from 'react'

import { ListCardHeader, type ListCardHeaderBadge } from '@/components/list-card-header'
import { InitialsAvatar } from '@/components/ui/initials-avatar'
import type { PersonListCardModel, PersonListCardVariant } from '@/lib/person-list-card-model'
import { cn } from '@/lib/utils'

export type PersonListCardIdentityProps = {
  person: PersonListCardModel
  variant: PersonListCardVariant
  headerAside?: ReactNode
  meta?: ReactNode
  menu?: ReactNode
  titleFallback?: ReactNode
  titleBadge?: ListCardHeaderBadge | null
}

export function PersonListCardIdentity({
  person,
  variant,
  headerAside,
  meta,
  menu,
  titleFallback,
  titleBadge,
}: PersonListCardIdentityProps) {
  const isDense = variant === 'compact' || variant === 'pipeline'
  const avatarClass = isDense ? 'h-9 w-9' : 'h-10 w-10'

  return (
    <div className="flex items-start gap-3">
      <InitialsAvatar
        src={person.avatarUrl}
        colorSeed={person.colorSeed}
        className={cn(avatarClass, 'shrink-0 border-0')}
        alt={person.name}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          {titleFallback ?? (
            <ListCardHeader
              title={person.name}
              badge={titleBadge}
              size={isDense ? 'sm' : 'lg'}
              className="min-w-0 flex-1 [&_[data-slot=card-title]]:break-normal [&_[data-slot=card-title]]:truncate"
            />
          )}
          {headerAside ? (
            <div
              className="min-w-0 max-w-[min(14rem,50%)] shrink"
              onClick={(e) => e.stopPropagation()}
            >
              {headerAside}
            </div>
          ) : null}
        </div>
        {person.subtitle ? (
          <p
            className="mt-0.5 line-clamp-1 text-xs text-muted-foreground"
            title={person.subtitle}
          >
            {person.subtitle}
          </p>
        ) : null}
        {meta ? <div className="mt-1">{meta}</div> : null}
      </div>
      {menu ? (
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          {menu}
        </div>
      ) : null}
    </div>
  )
}
